import { useState } from "react";
import { BRAND } from "../../../data/content";
import { isBudgetValidFor, OTHER_COUNTRY_VALUE } from "../../../data/formOptions";
import {
    loadGeo,
    getStatesOfCountrySync,
    getCitiesOfStateSync,
    countryNameSync,
    stateNameSync,
} from "../../../data/geoLoader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EMPTY = {
    full_name: "",
    email: "",
    phone: "",
    project_type: "",
    location_country: "",
    location_state: "",
    location_city: "",
    services: [],
    budget: "",
    timeline: "",
    message: "",
    consent: false,
    website: "",
};

function isEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function validate(form) {
    const errors = {};
    if (!form.full_name.trim()) errors.full_name = "Your full name, please.";
    if (!form.email.trim()) errors.email = "Email is required.";
    else if (!isEmail(form.email)) errors.email = "That email looks off.";
    if (!form.project_type) errors.project_type = "Select a project type.";

    // Location — country always required. State/city required only when the
    // country actually has that granularity in the library.
    if (!form.location_country)
        errors.location_country = "Select a country.";
    const isOther = form.location_country === OTHER_COUNTRY_VALUE;
    if (isOther) {
        if (!form.location_state.trim())
            errors.location_state = "Enter a region or province.";
    } else if (form.location_country) {
        const states = getStatesOfCountrySync(form.location_country);
        if (states.length > 0 && !form.location_state) {
            errors.location_state = "Select a state or region.";
        }
        if (form.location_state) {
            const cities = getCitiesOfStateSync(
                form.location_country,
                form.location_state,
            );
            if (cities.length > 0 && !form.location_city) {
                errors.location_city = "Select a city.";
            }
        }
    }

    if (!Array.isArray(form.services) || form.services.length < 1)
        errors.services = "Please select at least one service.";
    if (!form.timeline) errors.timeline = "Select a timeline.";
    if (!form.message.trim() || form.message.trim().length < 10)
        errors.message =
            "A few sentences about the project — at least ten characters.";
    if (!form.consent)
        errors.consent =
            "Please confirm you agree to be contacted about your inquiry.";
    return errors;
}

export function useInquiryForm() {
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");
    const [submitError, setSubmitError] = useState("");

    const setField = (name, value) => {
        setForm((f) => {
            const next = { ...f, [name]: value };
            // Clear stale budget when project type changes.
            if (
                name === "project_type" &&
                !isBudgetValidFor(value, next.budget)
            ) {
                next.budget = "";
            }
            // Cascade reset: country change → clear state + city.
            if (name === "location_country") {
                next.location_state = "";
                next.location_city = "";
            }
            // Cascade reset: state change → clear city.
            if (name === "location_state") {
                next.location_city = "";
            }
            return next;
        });
        if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        // Make sure the geo library is loaded so validation + payload
        // transformation both have full data (LocationFields typically
        // triggers this on mount; the await here is a no-op if cached).
        await loadGeo();

        const v = validate(form);
        setErrors(v);
        if (Object.keys(v).length > 0) return;

        setStatus("loading");
        setSubmitError("");

        // Resolve ISO codes → human-readable names for the backend payload.
        const isOther = form.location_country === OTHER_COUNTRY_VALUE;
        const countryName = isOther
            ? "Other"
            : countryNameSync(form.location_country);
        const stateName = isOther
            ? form.location_state
            : stateNameSync(form.location_state, form.location_country);

        try {
            const res = await fetch(`${API}/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    full_name: form.full_name,
                    email: form.email,
                    phone: form.phone || null,
                    project_type: form.project_type,
                    location_country: countryName,
                    location_state: stateName || null,
                    location_city: form.location_city || null,
                    services: form.services,
                    budget: form.budget || null,
                    timeline: form.timeline,
                    message: form.message,
                    consent: form.consent,
                    website: form.website,
                }),
            });
            if (!res.ok) throw new Error("Submission failed");
            setStatus("success");
        } catch {
            setStatus("error");
            setSubmitError(
                `Something went wrong. Please try again or email us directly at ${BRAND.email}.`,
            );
        }
    };

    const reset = () => {
        setForm(EMPTY);
        setErrors({});
        setStatus("idle");
        setSubmitError("");
    };

    return { form, errors, status, submitError, setField, onSubmit, reset };
}
